import { Component, Suspense, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Box, CircularProgress, useMediaQuery } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useWhoami } from '../api/whoami';
import { buildNav, isDualRole, workspaceLabelKey } from './nav';
import type { ShellOutletContext, WorkspaceView } from './nav';
import { InShellContext } from './pageHeader';
import { Sidebar } from './Sidebar';
import { SidebarRoleSwitcher } from './SidebarRoleSwitcher';
import { TopBar } from './TopBar';
import { Footer } from './Footer';
import { ImpersonationBanner } from './ImpersonationBanner';
import { OopsPage } from '../pages/OopsPage';
import { PreferencesDialogProvider } from '../profile/components/PreferencesDialogProvider';

interface RouteErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary around the routed content. React error boundaries must be
 * class components — there is no hook equivalent — so this is the shell's one
 * class. When a descendant page throws during render it swaps in `OopsPage`
 * (keeping the surrounding chrome intact), which makes `/oops` meaningful for
 * render-time failures rather than only the standalone public route.
 */
class RouteErrorBoundary extends Component<{ children: ReactNode }, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Never swallow silently — surface the crash for diagnostics. This becomes
    // an Application Insights track once SPA telemetry lands.
    console.error('Unhandled render error in routed content', error, info);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <OopsPage />;
    }
    return this.props.children;
  }
}

/**
 * Authenticated application chrome. Composes (top → bottom, left → right):
 *   ImpersonationBanner (only while impersonating) · Sidebar · TopBar ·
 *   routed content (`<Outlet/>`) · Footer.
 *
 * `whoami` is the single source of truth: the nav set and workspace label are
 * derived from the effective role + module settings, the banner from the
 * impersonation block, and the breadcrumb/user-menu from identity. The shell
 * renders its structure immediately (empty nav) while `whoami` loads so there
 * is no layout flash; guards and pages resolve against the same cached query.
 */
export function AppShell() {
  // Sidebar → hamburger drawer below 960px (prototype `useViewport` sm).
  const isMobile = useMediaQuery('(max-width:959.95px)');
  const [mobileOpen, setMobileOpen] = useState(false);
  // Dual-role internal staff can switch workspace; single-role users ignore it.
  const [view, setView] = useState<WorkspaceView>('admin');

  const { data: whoami } = useWhoami();
  const dualRole = isDualRole(whoami);
  const activeView = dualRole ? view : undefined;
  const navItems = whoami ? buildNav(whoami, activeView) : [];
  const wsLabelKey = workspaceLabelKey(whoami, activeView);

  return (
    <InShellContext value={true}>
      <PreferencesDialogProvider>
        {/* The shell is exactly the viewport and does not scroll; only <main>
            does. `minHeight: 100vh` used to sit here and on the content column,
            which is a floor rather than a ceiling — tall content grew the column,
            the row, and with it the sidebar, so the account menu docked at the
            sidebar's foot ended up at the foot of the *document* and fell below
            the fold (BUG-018). The `overflowY: auto` on <main> could never engage
            because nothing bounded its height. */}
        <Box
          sx={{
            height: '100vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
          }}
        >
          {whoami?.isImpersonating && whoami.impersonation && (
            <ImpersonationBanner
              impersonation={whoami.impersonation}
              adminEmail={whoami.authenticated.email}
            />
          )}
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <Sidebar
              items={navItems}
              customer={whoami?.customer ?? null}
              workspaceLabelKey={wsLabelKey}
              topSlot={
                dualRole ? <SidebarRoleSwitcher view={view} onChange={setView} /> : undefined
              }
              isMobile={isMobile}
              mobileOpen={mobileOpen}
              onMobileClose={() => setMobileOpen(false)}
            />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                // Not 100vh: the column must be free to be shorter than the
                // viewport (the impersonation banner takes a slice off the top)
                // and must never be taller, or the shell scrolls again. `0` lets
                // the flex child shrink so <main> is the one that overflows.
                minHeight: 0,
              }}
            >
              <TopBar
                navItems={navItems}
                whoami={whoami ?? null}
                isMobile={isMobile}
                onMenuClick={() => setMobileOpen(true)}
              />
              <Box
                component="main"
                sx={{
                  flex: 1,
                  // Pairs with flex: 1 — without it a flex item refuses to shrink
                  // below its content, so overflowY never has anything to clip.
                  minHeight: 0,
                  overflowY: 'auto',
                  px: { xs: '16px', md: '28px' },
                  py: { xs: '18px', md: '24px' },
                }}
              >
                <RouteErrorBoundary>
                  <Suspense
                    fallback={
                      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
                        <CircularProgress />
                      </Box>
                    }
                  >
                    <Outlet context={{ workspace: activeView } satisfies ShellOutletContext} />
                  </Suspense>
                </RouteErrorBoundary>
                <Footer />
              </Box>
            </Box>
          </Box>
        </Box>
      </PreferencesDialogProvider>
    </InShellContext>
  );
}
